import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { Enable2FASetup } from '../Enable2FASetup/Enable2FASetup';
import { ReactComponent as LockIcon } from '../Enable2FASetup/assets/lock.svg';
import { ReactComponent as CheckIcon } from '../Enable2FASetup/assets/check.svg';
import { API } from '../../API';
import { showRequestError } from '../../utils/requestError';
import styles from './LoginPage2FASetup.module.scss';

const STEP_INFO = 'info';
const STEP_SETUP = 'setup';
const STEP_SUCCESS = 'success';

export const LoginPage2FASetup = ({ onComplete, initialOtpData = null, onConfirmCode = null }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [step, setStep] = useState(STEP_INFO);
  // Login-driven enrollment: the secret/QR already came from POST /API/v1/token,
  // so seed it here and reuse it when the user advances to the setup step.
  const [otpData, setOtpData] = useState(initialOtpData);
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleEnableClick = useCallback(() => {
    // Login-driven enrollment: the secret/QR was already issued by
    // POST /API/v1/token and there is no access token yet, so calling /2fa
    // here would 401 and kick the user back to the login form. Reuse the
    // seeded secret and just advance to the setup step.
    if (otpData) {
      setStep(STEP_SETUP);
      return;
    }

    setLoading(true);
    API.fetch('POST', '/API/v1/2fa')
      .then((data) => {
        setOtpData(data);
        setStep(STEP_SETUP);
      })
      .catch((err) => showRequestError(err, t('2FA setup')))
      .finally(() => setLoading(false));
  }, [otpData, t]);

  const handleConfirm = useCallback(() => {
    const codeStr = code.join('');
    if (codeStr.length < 6) return;

    setLoading(true);
    setError(null);
    // Login-driven mode completes enrollment by re-submitting POST /API/v1/token
    // with the OTP (via onConfirmCode); the voluntary mode confirms via /2fa.
    const confirmation = onConfirmCode
      ? onConfirmCode(codeStr)
      : API.fetch('POST', '/API/v1/2fa/confirmation', null, {
          one_time_password: codeStr,
        });

    confirmation
      .then(() => {
        setStep(STEP_SUCCESS);
      })
      .catch(() => {
        // Pass the raw translation key; Enable2FASetup resolves it via t(error).
        setError('Incorrect code. Check the code in your authenticator app and try again');
      })
      .finally(() => setLoading(false));
  }, [code, onConfirmCode, t]);

  const handleDone = useCallback(() => {
    if (onComplete) {
      onComplete();
    } else {
      const from = location.state?.from?.pathname ?? '/';
      navigate(from, { replace: true });
    }
  }, [onComplete, navigate, location]);

  if (step === STEP_INFO) {
    return (
      <div className={styles.wrapper} translate="no">
        <div className={styles.cardSmall}>
          <div className={styles.infoContent}>
            <div className={styles.iconWrapperLock}>
              <LockIcon />
            </div>
            <h1 className={styles.title}>
              {t('Enable two-factor authentication')}
            </h1>
            <p className={styles.description}>
              {t('To protect your account, Osavul requires two-factor authentication. Setup takes about 2 minutes.')}
            </p>
            <button
              type="button"
              className={styles.primaryButton}
              onClick={handleEnableClick}
              disabled={loading}
            >
              {t('Enable 2FA')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (step === STEP_SUCCESS) {
    return (
      <div className={styles.wrapper} translate="no">
        <div className={styles.cardSmall}>
          <div className={styles.infoContent}>
            <div className={styles.iconWrapperSuccess}>
              <CheckIcon />
            </div>
            <h1 className={styles.title}>
              {t('Two-factor authentication enabled')}
            </h1>
            <p className={styles.description}>
              {t('Your account is now protected.')}
            </p>
            <button
              type="button"
              className={styles.primaryButton}
              onClick={handleDone}
            >
              {t('Done')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.wrapper} translate="no">
      <div className={styles.cardLarge}>
        <div className={styles.setupContent}>
          <h1 className={styles.setupTitle}>
            {t('Enable two-factor authentication')}
          </h1>
          <Enable2FASetup
            otpData={otpData}
            code={code}
            onCodeChange={setCode}
            error={error}
          />
        </div>
        <div className={styles.setupFooter}>
          <button
            type="button"
            className={styles.confirmButton}
            onClick={handleConfirm}
            disabled={code.join('').length < 6 || loading}
          >
            {t('Confirm')}
          </button>
        </div>
      </div>
    </div>
  );
};
